// Typed client for the Lomi Signal Engine (feedback / data-collection).
// Server: student-agent-api/routers/signal_router.py mounted at /feedback.
// One capture API shared with the bot; the web is just another adapter.
// See docs/feedback_engine_design.md.

import api from './api';

export type FieldType = 'scale' | 'choice' | 'boolean' | 'text';

export interface FieldDef {
  key: string;
  type: FieldType;
  min?: number;
  max?: number;
  options?: string[];
  label_en?: string;
  label_am?: string;
}

export interface SignalCampaign {
  id: string;
  key: string;
  name: string;
  campaign_type: string;
  question_en: string | null;
  question_am: string | null;
  fields: FieldDef[] | null;
  modalities: string[];
  reward_type: string;
}

export interface ActiveResponse {
  request_id?: string;
  campaign: SignalCampaign | null;
}

export interface SubmitPayload {
  campaign_id?: string | null;
  request_id?: string | null;
  modality: 'tap' | 'rating' | 'text' | 'structured' | 'voice';
  surface?: string;
  rating?: number | null;
  answers?: Record<string, unknown> | null;
  text?: string | null;
  context?: Record<string, unknown> | null;
  consent?: boolean;
}

export interface VoiceOptions {
  campaign_id?: string | null;
  request_id?: string | null;
  surface?: string;
  duration_s?: number;
  transcript?: string;
  consent?: boolean;
}

// Server echoes the real grant outcome so the surface shows the truth (a tap-only
// rating does NOT earn Founding Student — only a genuine voice/text contribution does).
export interface SubmitResult {
  ok: boolean;
  submission_id?: string;
  transcript?: string;
  error?: string;
  founding_student?: boolean;
  newly_founding?: boolean;
}

export const feedbackAPI = {
  // Pull: is there an ask for this student on this surface right now?
  getActive: async (surface = 'web_inflow', trigger?: string): Promise<ActiveResponse> => {
    const params: Record<string, string> = { surface };
    if (trigger) params.trigger = trigger;
    const res = await api.get<ActiveResponse>('/feedback/active', { params });
    return res.data;
  },

  submit: async (payload: SubmitPayload): Promise<SubmitResult> => {
    const res = await api.post('/feedback/submit', payload);
    return res.data;
  },

  // Voice note (browser MediaRecorder blob → multipart). Whisper runs server-side.
  submitVoice: async (blob: Blob, opts: VoiceOptions = {}): Promise<SubmitResult> => {
    const fd = new FormData();
    const t = blob.type || '';
    const ext = t.includes('mp4') || t.includes('m4a') ? 'mp4' : t.includes('ogg') ? 'ogg' : 'webm';
    fd.append('audio', blob, `feedback.${ext}`);
    if (opts.campaign_id) fd.append('campaign_id', opts.campaign_id);
    if (opts.request_id) fd.append('request_id', opts.request_id);
    fd.append('surface', opts.surface || 'web_inflow');
    if (opts.duration_s != null) fd.append('duration_s', String(opts.duration_s));
    if (opts.transcript) fd.append('transcript', opts.transcript);
    fd.append('consent', String(opts.consent !== false));
    const res = await api.post('/feedback/voice', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  // Persistent status for the profile badge (founding_student survives across sessions)
  // and the "Share your Lomi story" surface (volunteer_tier shows the signed-up state).
  getVolunteer: async (): Promise<{
    volunteered: boolean;
    founding_student?: boolean;
    status?: string;
    volunteer_tier?: 'call' | 'video' | 'both' | null;
    preferred_contact?: string | null;
    story_eligible?: boolean;
  }> => {
    const res = await api.get('/student/research/volunteer');
    return res.data;
  },

  // "Share your Lomi story" opt-in. Records WHAT the student volunteered for
  // (call | video | both) + the callback number they want (asked, never
  // prefilled — a Telegram/auth number is often not the one they want a call
  // on). Upserts one row per student.
  volunteer: async (
    tier: 'call' | 'video' | 'both',
    callbackPhone: string,
    callbackName = '',
    source = 'web_profile',
  ): Promise<{ ok: boolean; volunteered: boolean; volunteer_tier?: string; has_phone?: boolean }> => {
    const res = await api.post('/student/research/volunteer', {
      volunteer_tier: tier,
      callback_phone: callbackPhone,
      callback_name: callbackName || undefined,
      source,
    });
    return res.data;
  },

  markOpen: async (requestId: string): Promise<void> => {
    try {
      await api.post(`/feedback/request/${requestId}/open`);
    } catch {
      /* funnel telemetry — non-critical */
    }
  },

  markDismiss: async (requestId: string): Promise<void> => {
    try {
      await api.post(`/feedback/request/${requestId}/dismiss`);
    } catch {
      /* funnel telemetry — non-critical */
    }
  },
};

export default feedbackAPI;
